import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';

import {
  BehaviorSubject, combineLatest, Observable, of, Subject, delay, filter, finalize,
  first, map, pairwise, switchMap, take, tap
} from 'rxjs';

import {
  Address, InterceptorUtil, OCC_USER_ID_ANONYMOUS, OccEndpointsService, Product,
  ProductService, USE_CLIENT_TOKEN, UserIdService, WindowRef
} from '@spartacus/core';

import {
  Cart, CartModification, CartModificationList, CartRemoveEntrySuccessEvent, CartUpdateEntryFailEvent,
  CartUpdateEntrySuccessEvent, DeliveryMode, OrderEntry
} from '@spartacus/cart/base/root';

import { ActiveCartService, MultiCartService } from '@spartacus/cart/base/core';
import { Order, OrderFacade } from '@spartacus/order/root';

import { StatusEnum } from '@seb/web-design-system/core';

import { ExternalLogisticianEnum, OrderTypeEnum, StockStatusEnum } from '../enums';
import { Bundle, CartGroupEntry, PaymentModeList, ProductList } from '../models';
import { AbandonedCartTimerService } from './abandoned-cart-timer.service';
import { CheckoutAddressService } from './checkout-address.service';
import { ConfigurationService } from './configuration.service';
import { RelayPointService } from './relay-point.service';
import { BaseStoreService } from './base-store.service';
import { TrackingService } from './tracking.service';
import { ToasterService } from './toaster.service';
import { LoaderService } from './loader.service';
import { cities } from '../constants';

const ADD_TO_CART_CONTEXT = 'addToCartContext';

type ForfaitChangeEvent = {
  forfait: Product;
  quantity: number;
  statusCode: string;
};

type CartChangeEvent = {
  product?: Product;
  bundle?: Bundle;
  quantity: number;
  forfait?: string;
};

type BundleChangeEvent = {
  bundle: Bundle;
  quantity: number;
  statusCode: string;
  quantityAdded?: number;
};

type WaitOptions = {
  startAbandonedTimer?: boolean;
  isLoggedIn?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class CartFacadeService {
  private addForfaitToCartSubject = new Subject<ForfaitChangeEvent>();
  private addBundleToCartSubject = new Subject<BundleChangeEvent>();
  private toggleCartSubject = new Subject<CartChangeEvent>();
  private actionQueue = new BehaviorSubject<string[]>([]);

  public get addForfaitToCart$(): Observable<ForfaitChangeEvent> {
    return this.addForfaitToCartSubject.asObservable();
  }

  public get addBundleToCart$(): Observable<BundleChangeEvent> {
    return this.addBundleToCartSubject.asObservable();
  }

  public get toggleCart$(): Observable<CartChangeEvent> {
    return this.toggleCartSubject.asObservable();
  }

  constructor(
    private readonly http: HttpClient,
    private readonly winRef: WindowRef,
    private readonly orderFacade: OrderFacade,
    private readonly userIdService: UserIdService,
    private readonly loaderService: LoaderService,
    private readonly toasterService: ToasterService,
    private readonly productService: ProductService,
    private readonly trackingService: TrackingService,
    private readonly baseStoreService: BaseStoreService,
    private readonly multiCartService: MultiCartService,
    private readonly configService: ConfigurationService,
    private readonly relayPointService: RelayPointService,
    private readonly activeCartService: ActiveCartService,
    private readonly occEndpointsService: OccEndpointsService,
    private readonly checkoutAddressService: CheckoutAddressService,
    private readonly abandonedCartTimerService: AbandonedCartTimerService
  ) {
    this.abandonedCartTimerService.init(this.activeCartService.getEntries());

    this.activeCartService.getActive().pipe(
      pairwise(),
      filter(([previousCart, currentCart]) => (
        previousCart.code === currentCart.code &&
        previousCart.entryGroups?.length > 0 &&
        currentCart.entryGroups?.length === 0
      )),
      filter(([, currentCart]) => !!currentCart.deliveryAddress),
      switchMap(() => this.deleteDeliveryAddress())
    ).subscribe();
  }

  private static isProductPFS(product: Product): boolean {
    return product.externalLogistician === ExternalLogisticianEnum.PFS;
  }

  private static isCartPFS(cart: Cart): boolean {
    return cart.orderType === OrderTypeEnum.EXTLOG;
  }

  getPaymentModes(): Observable<PaymentModeList> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('paymentModes', { urlParams: { userId, cartId } });
      return this.http.get<PaymentModeList>(url);
    });
  }

  setPaymentMode(paymentModeId: string): Observable<void> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('paymentMode', {
        urlParams: { userId, cartId },
        queryParams: { paymentModeId }
      });
      return this.http.put<void>(url, undefined);
    }, {
      startAbandonedTimer: true
    });
  }

  checkoutAsGuest(): Observable<boolean> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('checkoutGuest', {
        urlParams: { userId, cartId }
      });
      return this.http.post<boolean>(url, '');
    });
  }

  openCartCompatibility(productCode: string, quantity: number, isForfait = false): void {
    this.productService.get(productCode).pipe(
      filter((p) => !!p),
      take(1)
    ).subscribe((product) => {
      if (isForfait) {
        this.notifyCartChanges(quantity, product, undefined, 'cart.modal.compatibilityCart.forfait.cartIsForfait');
      } else {
        this.notifyCartChanges(quantity, product);
      }
    });
  }

  addBundleToCart(bundle: Bundle, quantity: number, forceEmpty = false): Observable<CartModification[]> {
    let cartId: string;
    let limitedQuantity: number;

    this.abandonedCartTimerService.initSessionTimer();

    return combineLatest([
      this.activeCartService.requireLoadedCart(),
      this.userIdService.getUserId()
    ]).pipe(
      take(1),
      switchMap(([cartState, userId]) => {
        cartId = userId === 'current' ? cartState?.code : cartState?.guid;
        bundle.products.forEach((product) => {
          if (quantity > product.stock?.stockLevel) {
            if (!limitedQuantity || limitedQuantity > product.stock?.stockLevel) {
              limitedQuantity = product.stock.stockLevel;
            }
          }
        });
        const url = this.occEndpointsService.buildUrl('addBundle', {
          urlParams: { userId, cartId },
          queryParams: { bundleID: bundle?.id, qty: limitedQuantity || quantity, forceEmpty }
        });
        return this.http.post<CartModificationList>(url, {});
      }),
      tap((result) => {
        let statusCode: string;
        let quantityAdded: number;

        if (result.cartModifications?.length > 0) {
          result.cartModifications.forEach((cartModification: CartModification) => {
            if (!quantityAdded) {
              quantityAdded = cartModification.quantityAdded;
            } else if (quantityAdded > cartModification.quantityAdded) {
              quantityAdded = cartModification.quantityAdded;
            }

            if (statusCode === undefined) {
              statusCode = cartModification.statusCode;
            } else if (
              cartModification.statusCode === StockStatusEnum.OUT_OF_STOCK ||
              cartModification.statusCode === StockStatusEnum.NO_STOCK ||
              cartModification.statusCode === StockStatusEnum.MAX_ORDER
            ) {
              statusCode = cartModification.statusCode;
            } else if (
              cartModification.statusCode === StockStatusEnum.LOW_STOCK &&
              statusCode !== StockStatusEnum.OUT_OF_STOCK &&
              statusCode !== StockStatusEnum.NO_STOCK &&
              statusCode !== StockStatusEnum.MAX_ORDER
            ) {
              statusCode = cartModification.statusCode;
            } else if (
              cartModification.statusCode === StockStatusEnum.IN_STOCK &&
              statusCode !== StockStatusEnum.OUT_OF_STOCK &&
              statusCode !== StockStatusEnum.NO_STOCK &&
              statusCode !== StockStatusEnum.LOW_STOCK &&
              statusCode !== StockStatusEnum.MAX_ORDER
            ) {
              statusCode = cartModification.statusCode;
            }
          });
        }

        this.notifyBundleChanges(quantity, bundle, statusCode, quantityAdded);
        this.multiCartService.reloadCart(cartId);
      }),
      map((result) => result.cartModifications)
    );
  }

  addToCartShippingType(product: Product, quantity: number): void {
    const activeCart$ = this.activeCartService.getActive();

    this.abandonedCartTimerService.initSessionTimer();
    this.loaderService.showGlobalLoader();

    activeCart$.pipe(take(1)).subscribe((cart) => {
      const isGenreForfait = product.productGenre === 'Forfait';
      const { cartRestriction } = this.configService.config;

      if (isGenreForfait && cart.isForfait) {
        this.notifyCartChanges(quantity, product, undefined, 'cart.modal.compatibilityCart.forfait.cartMoreForfait');
      } else if (isGenreForfait && cart.entryGroups?.length > 0) {
        this.notifyCartChanges(quantity, product, undefined, 'cart.modal.compatibilityCart.forfait.cartNotEmpty');
      } else if (!isGenreForfait && cart.isForfait) {
        this.notifyCartChanges(quantity, product, undefined, 'cart.modal.compatibilityCart.forfait.cartIsForfait');
      } else if (
        cartRestriction &&
        CartFacadeService.isProductPFS(product) !== CartFacadeService.isCartPFS(cart) &&
        cart.entryGroups?.length > 0
      ) {
        this.notifyCartChanges(quantity, product);
      } else if (isGenreForfait) {
        this.addForfaitToCart(product, quantity).subscribe({
          error: (error) => {
            let status: string;
            if (error.error?.errors?.length > 0) {
              status = error.error.errors[0].type;
            }
            if (status === 'NotEmptyCartError') {
              this.notifyCartChanges(quantity, product, undefined, 'cart.modal.compatibilityCart.forfait.cartNotEmpty');
            } else {
              this.notifyForfaitChanges(quantity, status, product);
            }
          }
        });
      } else {
        this.activeCartService.addEntry(product.code, quantity);
      }
    });
  }

  addBundleToCartShippingType(bundle: Bundle, quantity: number): void {
    this.abandonedCartTimerService.initSessionTimer();
    this.loaderService.showGlobalLoader();

    this.activeCartService.getActive().pipe(take(1)).subscribe((cart) => {
      const { cartRestriction } = this.configService.config;
      if (cart.isForfait) {
        this.notifyCartChanges(quantity, undefined, bundle, 'cart.modal.compatibilityCart.forfait.cartIsForfait');
      } else if (cartRestriction && !CartFacadeService.isCartPFS(cart) && cart.entryGroups?.length > 0) {
        this.notifyCartChanges(quantity, undefined, bundle);
      } else {
        this.addBundleToCart(bundle, quantity).subscribe({
          error: (error) => {
            let status: string;
            if (error.error?.errors?.length > 0) {
              status = error.error?.errors[0].type;
            }
            if (status === 'NotEmptyCartError') {
              this.notifyCartChanges(quantity, undefined, bundle);
            } else if (status === 'UniqueItemInCartError') {
              this.notifyCartChanges(quantity, undefined, bundle, 'cart.modal.compatibilityCart.forfait.cartIsForfait');
            } else {
              this.notifyBundleChanges(quantity, bundle, status);
            }
          }
        });
      }
    });
  }

  forceEmptyCart(productCode: string, qty: number): Observable<boolean> {
    return this.forceCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('addEntriesEmpty', {
        urlParams: { userId, cartId },
        queryParams: { code: productCode, qty, forceEmpty: true }
      });
      return this.http.post<boolean>(url, '');
    });
  }

  updateBundle(bundleId: string, quantity: number): Observable<CartModificationList> {
    return this.forceCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('updateEntriesBundle', {
        urlParams: { userId, cartId, bundleId },
        queryParams: { qty: quantity }
      });
      return this.http.patch<CartModificationList>(url, '');
    }, {
      startAbandonedTimer: true
    });
  }

  deleteActiveCart(): void {
    this.forceCartUserCommand((userId, cartId) => {
      this.multiCartService.deleteCart(cartId, userId);
      return of(undefined);
    }).subscribe();
  }

  getSupportedDeliveryModes(): Observable<DeliveryMode[]> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('deliveryModes', {
        urlParams: { userId, cartId }
      });
      return this.http.get<{ deliveryModes: DeliveryMode[] }>(url).pipe(
        map((result) => result.deliveryModes)
      );
    });
  }

  setDeliveryMode(deliveryModeId: string): Observable<void> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('deliveryMode', {
        urlParams: { userId, cartId },
        queryParams: { deliveryModeId }
      });
      return this.http.put<void>(url, undefined);
    }, {
      startAbandonedTimer: true
    });
  }

  setDeliveryAddress(address: Address): Observable<boolean> {
    return this.queueCommand(() => this.checkoutAddressService.setDeliveryAddress(address));
  }

  createAndSetDeliveryAddress(address: Address): Observable<Address> {
    return this.queueCommand(() => this.checkoutAddressService.createAndSetDeliveryAddress(address));
  }

  setBillingAddress(address: Address): Observable<boolean> {
    return this.queueCommand(() => this.checkoutAddressService.setBillingAddress(address));
  }

  createAndSetBillingAddress(address: Address): Observable<Address> {
    return this.queueCommand(() => this.checkoutAddressService.createAndSetBillingAddress(address));
  }

  deleteDeliveryAddress(): Observable<boolean> {
    return this.queueCommand(() => this.checkoutAddressService.deleteDeliveryAddress());
  }

  deleteWithdrawalAddress(): Observable<void> {
    return this.queueCommand(() => this.checkoutAddressService.deleteWithdrawalAddress());
  }

  assignEmailToGuestUser(email: string): Observable<boolean> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('checkoutGuest', {
        urlParams: { userId, cartId },
        queryParams: { email }
      });
      return this.http.put<boolean>(url, '');
    }, {
      startAbandonedTimer: true
    });
  }

  getCartCrossSellProducts(): Observable<ProductList> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('cartCrossSell', {
        urlParams: { userId, cartId }
      });
      return this.http.get<ProductList>(url, {
        headers: (userId === OCC_USER_ID_ANONYMOUS)
          ? InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true)
          : new HttpHeaders()
      });
    });
  }

  addForfaitToCart(forfait: Product, quantity: number, pointOfService?: string): Observable<CartModification[]> {
    this.abandonedCartTimerService.initSessionTimer();

    return combineLatest([
      this.activeCartService.requireLoadedCart(),
      this.userIdService.getUserId()
    ]).pipe(
      take(1),
      switchMap(([cartState, userId]) => {
        const cartId = (userId === 'current') ? cartState?.code : cartState?.guid;
        const { defaultDeliveryOrigin, market } = this.baseStoreService.baseStore;
        const defaultPointOfService = defaultDeliveryOrigin?.name;

        if (!pointOfService && !defaultPointOfService) {
          this.notifyError('cart.noDefaultRepairerTitle', 'cart.noDefaultRepairerDescription');
          return of([]);
        }

        let pointOfServiceId$: Observable<string>;

        if (pointOfService) {
          // Si un relai est configuré, on le selectionne
          pointOfServiceId$ = of(pointOfService);
        } else if (forfait.shippingIncluded) {
          // Sinon si la livraison est offerte, on a besoin d'un relai colissimo
          let params: any = { deliveryProvider: 'COLISSIMO' };
          const myPickup = localStorage.getItem('myPickup');

          if (myPickup) {
            const parsed = JSON.parse(myPickup);
            params = { ...params, ...parsed };
          } else {
            params = { ...params, ...cities[market], country: market };
          }

          pointOfServiceId$ = this.relayPointService.getRelayPoints(params).pipe(
            map(([relay]) => relay?.id)
          );
        } else {
          pointOfServiceId$ = of(defaultPointOfService);
        }

        return pointOfServiceId$.pipe(
          switchMap((point) => {
            const urlColis = this.occEndpointsService.buildUrl('addEntries', {
              urlParams: { userId, cartId },
              queryParams: { forceEmpty: !!pointOfService }
            });

            const body = {
              product: { code: forfait.code },
              quantity,
              deliveryPointOfService: { name: point }
            };

            return this.http.post<CartModification>(urlColis, body).pipe(
              tap((result) => {
                if (!result) {
                  this.notifyError('cart.noDefaultRepairerTitle', 'cart.noDefaultRepairerDescription');
                } else if (!pointOfService) {
                  this.notifyForfaitChanges(quantity, result.statusCode || undefined, forfait);
                  this.multiCartService.reloadCart(cartId);
                }
              }),
              map((result) => result?.cartModifications ?? [])
            );
          })
        );
      })
    );
  }

  cartProductUpdateEntrySuccess(event: CartUpdateEntrySuccessEvent): void {
    this.notifySuccess(
      'cart.toast.updateQuantityToasterTitle',
      'cart.toast.updateQuantityToasterDescription'
    );

    const quantityUpdated = event.quantity - event.entry.quantity;
    const cartContext = this.getAddToCartContext();
    let trackEvent = 'clickRemoveFromCart';

    const productEvent = this.trackingService.productToEventProduct(
      event.entry.product,
      undefined,
      Math.abs(quantityUpdated)
    );

    if (cartContext && quantityUpdated > 0) {
      productEvent.position = cartContext;
      trackEvent = 'clickAddToCart';
      this.removeAddToCartContext();
    }

    this.trackingService.sendEvent(trackEvent, productEvent);
  }

  cartProductUpdateEntryFail(event: CartUpdateEntryFailEvent | any): void {
    if (
      event?.error?.details?.length > 0 &&
      event.error.details[0].type === 'ProductTypeForCartError' &&
      event.error.details[0].message === 'allowedQuantityToUpdateExceeded'
    ) {
      this.notifyError(
        'notify.notifyErrorToastTitle',
        'cart.modal.add.' + event.error?.details[0].message
      );
    }
  }

  cartBundleUpdateEntrySuccess(eventQuantity: number, bundleEntry: CartGroupEntry): void {
    this.notifySuccess(
      'cart.toast.updateQuantityToasterTitle',
      'cart.toast.updateBundleQuantityToasterDescription'
    );

    const quantityUpdated = eventQuantity - bundleEntry.selectedQuantity;

    if (quantityUpdated > 0) {
      this.trackingService.sendEvent(
        'clickAddToCart',
        this.trackingService.mapToBundleEventClickFromCartGroupEntry(bundleEntry, quantityUpdated)
      );
    } else {
      this.trackingService.sendEvent(
        'clickRemoveFromCart',
        this.trackingService.mapToBundleEventClickFromCartGroupEntry(bundleEntry, Math.abs(quantityUpdated))
      );
    }
  }

  cartProductRemoveEntrySuccessEvent(event: CartRemoveEntrySuccessEvent): void {
    this.notifySuccess(
      'cart.toast.removeQuantityToasterTitle',
      'cart.toast.removeQuantityToasterDescription',
      false
    );

    this.trackingService.sendEvent(
      'clickRemoveFromCart',
      this.trackingService.productToEventProduct(event.entry.product, undefined, event.entry.quantity)
    );
  }

  cartBundleRemoveEntrySuccessEvent(bundleEntry: CartGroupEntry): void {
    this.notifySuccess(
      'cart.toast.removeQuantityToasterTitle',
      'cart.toast.removeBundleToasterDescription',
      false
    );

    this.trackingService.sendEvent(
      'clickRemoveFromCart',
      this.trackingService.mapToBundleEventClickFromCartGroupEntry(bundleEntry)
    );
  }

  cartProductAddEntrySuccessEvent(eventQuantity: number, product: Product): void {
    this.abandonedCartTimerService.initSessionTimer();

    this.trackingService.sendEvent(
      'clickAddToCart',
      this.trackingService.productToEventProduct(product, undefined, eventQuantity, this.getAddToCartContext())
    );

    this.removeAddToCartContext();
  }

  cartBundleAddEntrySuccessEvent(eventQuantity: number, bundle: Bundle): void {
    this.abandonedCartTimerService.initSessionTimer();

    this.trackingService.sendEvent(
      'clickAddToCart',
      this.trackingService.bundleToEventProduct(bundle, undefined, eventQuantity, this.getAddToCartContext())
    );

    this.removeAddToCartContext();
  }

  assignTaxNumberToCurrentUser(legalTaxNumber: string, isLoggedIn: boolean): Observable<boolean> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('taxNumber', {
        urlParams: { userId, cartId },
        queryParams: { legalTaxNumber, refreshCart: false }
      });
      return this.http.put<boolean>(url, '');
    }, {
      startAbandonedTimer: true,
      isLoggedIn
    });
  }

  validateAddressesBeforePaymentStep(isLoggedIn: boolean): Observable<boolean> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('validateAddress', {
        urlParams: { userId, cartId }
      });
      return this.http.put<boolean>(url, '');
    }, {
      isLoggedIn
    });
  }

  placeOrderService(): Observable<Order> {
    return this.queueCommand(() => this.orderFacade.placeOrder(true));
  }

  validateCartService(isLoggedIn: boolean): Observable<boolean> {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('validateCartForPlaceOrder', {
        urlParams: { userId, cartId },
        queryParams: { cartId }
      });

      const headers = (userId === OCC_USER_ID_ANONYMOUS)
        ? InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true)
        : new HttpHeaders();

      return this.http.put<boolean>(url, {}, { headers });
    }, {
      isLoggedIn
    });
  }

  getCartEntries(): Observable<OrderEntry[]> {
    return this.queueCommand(() => this.activeCartService.getEntries());
  }

  submitAbandonedCartEmail(userEmail: string) {
    return this.queueCartUserCommand((userId, cartId) => {
      const url = this.occEndpointsService.buildUrl('abandonedCart', {
        urlParams: { userId, cartId },
        queryParams: { email: userEmail }
      });
      const headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true);
      return this.http.put<boolean>(url, {}, { headers });
    });
  }

  private forceCartUserCommand<T>(
    cb: (userId: string, cartId: string) => Observable<T>,
    options?: WaitOptions
  ): Observable<T> {
    const userId$ = this.userIdService.takeUserId();
    const cartId$ = this.activeCartService.getActiveCartId();

    if (options?.startAbandonedTimer) {
      this.abandonedCartTimerService.initSessionTimer();
    }

    return combineLatest([userId$, cartId$]).pipe(
      filter(([userId, cartId]) => !!userId && !!cartId),
      take(1),
      switchMap(([userId, cartId]) => cb(userId, cartId))
    );
  }

  private queueCommand<T>(cb: () => Observable<T>) {
    const { wait$, actionId } = this.waitForQueue(true);

    return wait$.pipe(
      switchMap(() => cb()),
      finalize(() => this.removeFromQueue(actionId))
    );
  }

  private queueCartUserCommand<T>(
    cb: (userId: string, cartId: string) => Observable<T>,
    options?: WaitOptions
  ): Observable<T> {
    const { wait$, actionId } = this.waitForQueue(!!options?.startAbandonedTimer);
    const userId$ = this.userIdService.takeUserId(!!options?.isLoggedIn);
    const cartId$ = this.activeCartService.getActiveCartId();

    return wait$.pipe(
      switchMap(() => combineLatest([userId$, cartId$])),
      filter(([userId, cartId]) => !!userId && !!cartId),
      take(1),
      switchMap(([userId, cartId]) => cb(userId, cartId)),
      finalize(() => this.removeFromQueue(actionId))
    );
  }

  private addToQueue(actionId: string): Observable<any> {
    return this.actionQueue.pipe(
      take(1),
      tap((currentQueue) => this.actionQueue.next([...currentQueue, actionId]))
    );
  }

  private removeFromQueue(actionId: string): void {
    this.actionQueue.pipe(take(1)).subscribe((currentQueue) => {
      const queueIndex = currentQueue.indexOf(actionId);
      if (queueIndex > -1) {
        currentQueue.splice(queueIndex, 1);
        this.actionQueue.next(currentQueue);
      }
    });
  }

  private waitForQueue(startAbandonedTimer = false): { wait$: Observable<any>; actionId: string } {
    const actionId = uuid();

    if (startAbandonedTimer) {
      this.abandonedCartTimerService.initSessionTimer();
    }

    const wait$ = this.addToQueue(actionId).pipe(
      switchMap(() => combineLatest([this.activeCartService.isStable(), this.actionQueue])),
      first(([isStable, currentQueue]) => isStable && currentQueue[0] === actionId),
      delay(50)
    );

    return { wait$, actionId };
  }

  private getAddToCartContext(): string {
    return this.winRef.sessionStorage?.getItem(ADD_TO_CART_CONTEXT) || '';
  }

  private removeAddToCartContext(): void {
    this.winRef.sessionStorage?.removeItem(ADD_TO_CART_CONTEXT);
  }

  private notifySuccess(title: string, description: string, startAbandonedTimer = true): void {
    if (startAbandonedTimer) {
      this.abandonedCartTimerService.initSessionTimer();
    }

    this.toasterService.sendToasterEvent({
      status: StatusEnum.success,
      description,
      title
    });
  }

  private notifyError(title: string, description: string): void {
    this.toasterService.sendToasterEvent({
      status: StatusEnum.error,
      description,
      title
    });
  }

  private notifyCartChanges(quantity: number, product?: Product, bundle?: Bundle, forfait = ''): void {
    this.toggleCartSubject.next({ quantity, product, bundle, forfait });
  }

  private notifyForfaitChanges(quantity: number, statusCode: string, forfait?: Product): void {
    this.addForfaitToCartSubject.next({ forfait, quantity, statusCode });
  }

  private notifyBundleChanges(quantity: number, bundle: Bundle, statusCode: string, added?: number): void {
    const quantityAdded = (added && added !== quantity) ? added : undefined;
    this.addBundleToCartSubject.next({ quantity, bundle, statusCode, quantityAdded });
  }
}
